import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, Navigate, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import {
  buildWebCreatorPublicUrl,
  fetchWebCreatorSite,
  updateWebCreatorSite,
} from '../../api/webCreator';
import WebCreatorLayout from './WebCreatorLayout';
import SiteCommerceAdminPanel from './components/SiteCommerceAdminPanel';
import PublicLinkActions from './components/PublicLinkActions';
import { migrateSite } from './utils/defaults';
import { buildCommerceAdminTabs, siteHasCommerceAdmin } from './utils/commerce';
import { JUST_ONE_PAGE_NAME } from './justOnePageBrand';

export default function WebCreatorCommerceAdmin() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [site, setSite] = useState(null);
  const [message, setMessage] = useState('');
  const [tab, setTab] = useState('overview');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetchWebCreatorSite(id);
      const s = migrateSite(res?.data?.site);
      if (!s || s.isTemplate) {
        setSite(null);
        return;
      }
      setSite(s);
    } catch {
      setSite(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!site) return;
    const ids = buildCommerceAdminTabs(site).map((t) => t.id);
    if (!ids.includes(tab) && ids[0]) setTab(ids[0]);
  }, [site, tab]);

  const handleSave = async (patch) => {
    if (!id || !site) return;
    setSaving(true);
    setMessage('');
    try {
      const res = await updateWebCreatorSite(id, patch);
      setSite(migrateSite(res?.data?.site));
      setMessage('Modifications enregistrées.');
    } catch (err) {
      setMessage(err?.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <WebCreatorLayout>
        <div className="flex-1 flex items-center justify-center text-muted-foreground min-h-[50vh]">
          <Icon name="Loader2" size={22} className="animate-spin mr-2" />
          Chargement…
        </div>
      </WebCreatorLayout>
    );
  }

  if (!site) {
    return <Navigate to="/web-creator" replace />;
  }

  if (!siteHasCommerceAdmin(site)) {
    return <Navigate to={`/web-creator/edit/${id}`} replace />;
  }

  const editorUrl = `/web-creator/edit/${id}`;
  const adminTabs = buildCommerceAdminTabs(site);

  return (
    <WebCreatorLayout>
      <Helmet>
        <title>
          Administration — {site.title || 'Site'} · {JUST_ONE_PAGE_NAME}
        </title>
      </Helmet>

      <div className="h-full flex min-h-0 flex-1 bg-white dark:bg-neutral-950">
        <aside className="hidden lg:flex w-[260px] shrink-0 flex-col bg-white dark:bg-neutral-950">
          <div className="p-5">
            <Link
              to="/web-creator"
              className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors mb-4"
            >
              <Icon name="ArrowLeft" size={14} />
              Bibliothèque
            </Link>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-primary/80">
              Administration
            </p>
            <h1 className="text-base font-semibold text-foreground mt-1 truncate leading-snug">
              {site.title || 'Sans titre'}
            </h1>
            <span
              className={`inline-block mt-2 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${
                site.published
                  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {site.published ? 'En ligne' : 'Brouillon'}
            </span>
          </div>
          <nav className="flex-1 overflow-y-auto p-3 space-y-0.5" aria-label="Sections administration">
            {adminTabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors text-left ${
                  tab === t.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/70'
                }`}
              >
                <Icon name={t.icon} size={17} className="shrink-0 opacity-90" />
                {t.label}
              </button>
            ))}
          </nav>
          <div className="p-4 space-y-2">
            <Link to={editorUrl} className="block">
              <Button type="button" variant="outline" size="sm" className="w-full justify-center">
                <Icon name="Pencil" size={14} className="mr-1.5" />
                Éditeur de page
              </Button>
            </Link>
            {site.published && site.slug ? (
              <a
                href={buildWebCreatorPublicUrl(site.slug)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1 text-xs text-primary hover:underline py-1"
              >
                Page publique
                <Icon name="ExternalLink" size={12} />
              </a>
            ) : null}
          </div>
        </aside>

        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <header className="shrink-0 bg-white dark:bg-neutral-950 px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0 lg:hidden">
              <Link to="/web-creator" className="p-2 rounded-lg hover:bg-muted shrink-0">
                <Icon name="ArrowLeft" size={18} />
              </Link>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{site.title || 'Sans titre'}</p>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Administration
                </p>
              </div>
            </div>
            <div className="hidden lg:block min-w-0 flex-1">
              {site.slug ? (
                <PublicLinkActions
                  slug={site.slug}
                  published={site.published}
                  size="sm"
                />
              ) : null}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link to={editorUrl} className="lg:hidden">
                <Button type="button" variant="outline" size="sm">
                  <Icon name="Pencil" size={14} />
                </Button>
              </Link>
            </div>
          </header>

          {message ? (
            <div className="shrink-0 px-4 sm:px-6 py-2.5 text-sm bg-primary/10 text-primary">
              {message}
            </div>
          ) : null}

          <nav
            className="lg:hidden shrink-0 flex gap-1 overflow-x-auto px-4 py-2 bg-white dark:bg-neutral-950"
            aria-label="Sections administration"
          >
            {adminTabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  tab === t.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/60 text-muted-foreground'
                }`}
              >
                <Icon name={t.icon} size={14} />
                {t.label}
              </button>
            ))}
          </nav>

          <main className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6 py-5 sm:py-8 w-full bg-white dark:bg-neutral-950">
            <div className="w-full min-w-0">
              <SiteCommerceAdminPanel
                site={site}
                published={site.published}
                saving={saving}
                onSave={handleSave}
                tab={tab}
                onTabChange={setTab}
              />
            </div>
          </main>

          <footer className="shrink-0 px-4 py-2 text-[11px] text-muted-foreground bg-white dark:bg-neutral-950 lg:hidden">
            {JUST_ONE_PAGE_NAME} — commandes & réservations
          </footer>
        </div>
      </div>
    </WebCreatorLayout>
  );
}
